import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "./cn";
export type MenuRowOptions = {
  /** Leading icon; svgs render at 16px, muted until the row highlights. */
  icon?: React.ReactNode;
  /** Muted second line under the label. */
  description?: React.ReactNode;
  /** Muted content after the label, e.g. a username or a check. */
  trailing?: React.ReactNode;
};
export type MenuRowVariant = "default" | "destructive";
export const MENU_ROW_DESTRUCTIVE_CLASS = "text-red-600 focus:text-red-600 data-[highlighted]:text-red-600 dark:text-red-400 dark:focus:text-red-400 dark:data-[highlighted]:text-red-400";

// `group/menu-row` is set on the item root by the primitives. The icon keys
// off Radix's focus/data-[highlighted], never CSS :hover (DESIGN.md →
// "Dropdown item focus").
const ICON_TONE_CLASS = "text-neutral-500 group-focus/menu-row:text-neutral-900 group-data-[highlighted]/menu-row:text-neutral-900 dark:text-neutral-400 dark:group-focus/menu-row:text-white dark:group-data-[highlighted]/menu-row:text-white";

// h-[22px] is the label's line box, so the icon stays on the first line
// when a description grows the row.
export const MENU_ROW_ICON_CLASS = "flex h-[22px] shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0";
export function MenuRowIcon(t0) {
  const $ = _c(5);
  const {
    children,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "muted" : t1;
  const t2 = tone === "muted" ? ICON_TONE_CLASS : "text-current";
  let t3;
  if ($[0] !== t2) {
    t3 = cn(MENU_ROW_ICON_CLASS, t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <span aria-hidden={true} className={t3}>{children}</span>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export function hasMenuRowOptions({
  icon,
  description,
  trailing
}: MenuRowOptions) {
  return icon != null || description != null || trailing != null;
}
export function menuRowClassName(options: MenuRowOptions, variant: MenuRowVariant = "default") {
  return cn("group/menu-row", hasMenuRowOptions(options) && "gap-2", options.description != null && "items-start", variant === "destructive" && MENU_ROW_DESTRUCTIVE_CLASS);
}
function MenuRowContent(t0) {
  const $ = _c(12);
  const {
    icon,
    description,
    trailing,
    variant,
    children
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== variant) {
    t1 = icon != null && <MenuRowIcon tone={variant === "destructive" ? "current" : "muted"}>{icon}</MenuRowIcon>;
    $[0] = icon;
    $[1] = variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== description) {
    t2 = description != null ? <span className="flex min-w-0 flex-1 flex-col"><span className="truncate">{children}</span><span className="pb-0.5 type-body-xs-regular text-neutral-500 dark:text-neutral-300">{description}</span></span> : <span className="min-w-0 flex-1 truncate">{children}</span>;
    $[3] = children;
    $[4] = description;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== trailing) {
    t3 = trailing != null && <span className="flex h-[22px] shrink-0 items-center pl-3 text-neutral-500 dark:text-neutral-400">{trailing}</span>;
    $[6] = trailing;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}

/**
 * Lays out icon / label / description / trailing inside a menu row. Children
 * pass through untouched when no option is set. With `asChild`, the content
 * goes inside the child element (e.g. a link) so it stays the row itself:
 * `<Link>Settings</Link>` → `<Link><Icon/><span>Settings</span></Link>`.
 */
export function renderMenuRowChildren(children: React.ReactNode, options: MenuRowOptions, {
  asChild = false,
  variant = "default" as MenuRowVariant
} = {}): React.ReactNode {
  if (!hasMenuRowOptions(options)) return children;
  if (asChild && React.isValidElement<{
    children?: React.ReactNode;
  }>(children)) {
    return React.cloneElement(children, undefined, <MenuRowContent {...options} variant={variant}>
        {children.props.children}
      </MenuRowContent>);
  }
  return <MenuRowContent {...options} variant={variant}>
      {children}
    </MenuRowContent>;
}